export const siteConfig = Object.freeze({
  brandName: "Mobrinova",
  logo: null,
  tagline: "The new language of the cravat",
  announcement: "Complimentary delivery in Europe on orders over €180",
  description:
    "Modern men's neckwear shaped by Parisian restraint, tactile fabrics and thoughtful finishing.",
  contact: {
    email: "cash@mobrinova.online",
    address: "Paris, France",
    hours: "Monday–Friday, 10:00–18:00 CET",
  },
  currency: {
    code: "EUR",
    symbol: "€",
    locale: "en-IE",
  },
  currencyCode: "EUR",
  currencySymbol: "€",
  shipping: {
    freeThreshold: 180,
    deliveryFee: 12,
    expressFee: 24,
    deliveryWindow: "2–4 business days",
    returnsWindowDays: 30,
  },
});

const createProduct = ({
  id,
  name,
  category,
  price,
  image,
  description,
  dimensions = "128 × 18 cm",
  weave,
  hardware,
  tags,
  color,
  material,
  stock,
  featured = false,
  rating,
  reviews,
}) => ({
  id,
  name,
  category,
  price,
  image,
  images: [image],
  description,
  attributes: {
    dimensions,
    weave,
    hardware,
    origin: "Designed in Paris",
    care: "Specialist dry clean",
  },
  tags,
  size: ["One size"],
  colors: [color],
  material,
  stock,
  featured,
  rating,
  reviews,
  Features: [
    `${weave} construction`,
    `Adjustable ${hardware.toLowerCase()}`,
    "Soft hand-finished edges",
    "Presented in a reusable atelier box",
  ],
});

const productData = [
  createProduct({
    id: 1,
    name: "Midnight Passage Pleated Cravat",
    category: "Pleated Cravats",
    price: 165,
    image: "/img/p01-midnight.jpg",
    description: [
      "A deep midnight cravat cut with fine permanent pleats for a clean, sculptural drape.",
      "Its warm brass slider brings quiet definition to open-collar shirts and relaxed evening tailoring.",
    ],
    weave: "Micro-pleated satin",
    hardware: "Brushed brass slider",
    tags: ["midnight", "evening", "pleated", "signature"],
    color: "Midnight Navy",
    material: "Silk-blend satin",
    stock: 18,
    featured: true,
    rating: 4.9,
    reviews: 38,
  }),
  createProduct({
    id: 2,
    name: "Obsidian Line Pleated Cravat",
    category: "Pleated Cravats",
    price: 165,
    image: "/img/p02-obsidian.jpg",
    description: [
      "An obsidian-black cravat whose narrow pleats catch the light without losing its understated character.",
      "Designed as a modern alternative to the tie, it sits naturally beneath soft and structured collars.",
    ],
    weave: "Micro-pleated satin",
    hardware: "Antique brass slider",
    tags: ["black", "minimal", "pleated", "formal"],
    color: "Obsidian Black",
    material: "Silk-blend satin",
    stock: 14,
    featured: true,
    rating: 4.8,
    reviews: 31,
  }),
  createProduct({
    id: 3,
    name: "Claret Hour Silk Cravat",
    category: "Silk Cravats",
    price: 175,
    image: "/img/p03-wine.jpg",
    description: [
      "A wine-red silk cravat with a mellow lustre inspired by late dinners and candlelit rooms.",
      "The pleated body keeps its form while remaining supple enough for an easy, personal knot.",
    ],
    dimensions: "130 × 18 cm",
    weave: "Pleated silk twill",
    hardware: "Satin brass slider",
    tags: ["wine", "silk", "evening", "gift"],
    color: "Claret Wine",
    material: "100% silk twill",
    stock: 11,
    featured: true,
    rating: 4.9,
    reviews: 27,
  }),
  createProduct({
    id: 4,
    name: "Jardin Nocturne Pleated Cravat",
    category: "Pleated Cravats",
    price: 160,
    image: "/img/p04-forest.jpg",
    description: [
      "A saturated forest-green cravat that adds depth to cream shirting, tobacco linen and navy wool.",
      "Precisely set pleats create a generous fall without adding bulk at the neck.",
    ],
    weave: "Micro-pleated satin",
    hardware: "Aged brass slider",
    tags: ["forest", "green", "pleated", "autumn"],
    color: "Forest Green",
    material: "Silk-viscose satin",
    stock: 16,
    rating: 4.7,
    reviews: 19,
  }),
  createProduct({
    id: 5,
    name: "Porcelain Light Silk Cravat",
    category: "Silk Cravats",
    price: 175,
    image: "/img/p05-ivory.jpg",
    description: [
      "An ivory silk cravat with a soft pearl finish, made for summer ceremonies and tonal dressing.",
      "The pale surface reveals the rhythm of every pleat and frames darker jackets with quiet contrast.",
    ],
    dimensions: "130 × 18 cm",
    weave: "Pleated silk satin",
    hardware: "Pale gold slider",
    tags: ["ivory", "ceremony", "silk", "summer"],
    color: "Porcelain Ivory",
    material: "100% silk satin",
    stock: 9,
    featured: true,
    rating: 4.8,
    reviews: 24,
  }),
  createProduct({
    id: 6,
    name: "Graphite Studio Pleated Cravat",
    category: "Pleated Cravats",
    price: 155,
    image: "/img/p06-graphite.jpg",
    description: [
      "A graphite cravat balancing charcoal depth with a subtle mineral sheen.",
      "Its neutral tone moves easily between weekday tailoring and an unstructured weekend jacket.",
    ],
    weave: "Micro-pleated faille",
    hardware: "Gunmetal slider",
    tags: ["graphite", "neutral", "modern", "workwear"],
    color: "Graphite Grey",
    material: "Viscose-silk faille",
    stock: 22,
    rating: 4.7,
    reviews: 18,
  }),
  createProduct({
    id: 7,
    name: "Sapphire Current Silk Cravat",
    category: "Silk Cravats",
    price: 180,
    image: "/img/p07-sapphire.jpg",
    description: [
      "A vivid sapphire cravat in luminous silk, designed to sharpen pale blue, white and charcoal shirting.",
      "Fine pleating tempers the saturated colour with architectural texture.",
    ],
    dimensions: "130 × 18 cm",
    weave: "Pleated silk satin",
    hardware: "Polished brass slider",
    tags: ["sapphire", "blue", "silk", "statement"],
    color: "Sapphire Blue",
    material: "100% silk satin",
    stock: 12,
    featured: true,
    rating: 4.9,
    reviews: 35,
  }),
  createProduct({
    id: 8,
    name: "Burnished Bronze Evening Cravat",
    category: "Evening Cravats",
    price: 185,
    image: "/img/p08-bronze.jpg",
    description: [
      "A bronze evening cravat with warm metallic undertones and an elegant, controlled drape.",
      "It lends texture to black tailoring and brings richness to earth-toned formalwear.",
    ],
    dimensions: "128 × 19 cm",
    weave: "Pleated metallic satin",
    hardware: "Dark bronze slider",
    tags: ["bronze", "evening", "metallic", "occasion"],
    color: "Burnished Bronze",
    material: "Silk-lurex satin",
    stock: 8,
    rating: 4.8,
    reviews: 16,
  }),
  createProduct({
    id: 9,
    name: "Plum Salon Silk Cravat",
    category: "Silk Cravats",
    price: 175,
    image: "/img/p09-plum.jpg",
    description: [
      "A plum silk cravat with blue undertones, selected for its ability to feel expressive yet composed.",
      "Wear it close beneath a jacket or loosen the slider for a softer, scarf-like silhouette.",
    ],
    dimensions: "130 × 18 cm",
    weave: "Pleated silk twill",
    hardware: "Antique gold slider",
    tags: ["plum", "purple", "silk", "evening"],
    color: "Salon Plum",
    material: "100% silk twill",
    stock: 13,
    rating: 4.6,
    reviews: 14,
  }),
  createProduct({
    id: 10,
    name: "Camel Promenade Pleated Cravat",
    category: "Pleated Cravats",
    price: 155,
    image: "/img/p10-camel.jpg",
    description: [
      "A warm camel cravat designed around the easy palette of stone, ecru and chocolate tailoring.",
      "The matte pleated cloth offers definition without the ceremony of a conventional necktie.",
    ],
    weave: "Micro-pleated faille",
    hardware: "Brushed brass slider",
    tags: ["camel", "neutral", "daywear", "pleated"],
    color: "Warm Camel",
    material: "Viscose-silk faille",
    stock: 21,
    featured: true,
    rating: 4.8,
    reviews: 29,
  }),
  createProduct({
    id: 11,
    name: "Steel Horizon Pleated Cravat",
    category: "Pleated Cravats",
    price: 160,
    image: "/img/p11-steel.jpg",
    description: [
      "A cool steel-grey cravat with a clean sheen and precise, evenly spaced pleats.",
      "Its restrained colour works especially well with navy suiting and crisp white poplin.",
    ],
    weave: "Micro-pleated satin",
    hardware: "Brushed silver slider",
    tags: ["steel", "grey", "minimal", "formal"],
    color: "Steel Grey",
    material: "Silk-viscose satin",
    stock: 17,
    rating: 4.7,
    reviews: 21,
  }),
  createProduct({
    id: 12,
    name: "Ember Rust Textured Cravat",
    category: "Textured Cravats",
    price: 170,
    image: "/img/p12-rust.jpg",
    description: [
      "A rust-toned cravat woven with a dry, tactile hand that feels at home beside flannel and brushed cotton.",
      "Its ember colour gives autumn layers a focused point of warmth.",
    ],
    dimensions: "126 × 19 cm",
    weave: "Pleated silk-linen weave",
    hardware: "Aged brass slider",
    tags: ["rust", "textured", "autumn", "linen"],
    color: "Ember Rust",
    material: "Silk and linen blend",
    stock: 10,
    rating: 4.6,
    reviews: 12,
  }),
  createProduct({
    id: 13,
    name: "Tidal Teal Silk Cravat",
    category: "Silk Cravats",
    price: 175,
    image: "/img/p13-teal.jpg",
    description: [
      "A teal silk cravat that shifts gently between blue and green as the pleats move.",
      "The saturated tone gives quiet energy to sand, tobacco and midnight tailoring.",
    ],
    dimensions: "130 × 18 cm",
    weave: "Pleated silk satin",
    hardware: "Polished brass slider",
    tags: ["teal", "silk", "colour", "occasion"],
    color: "Tidal Teal",
    material: "100% silk satin",
    stock: 15,
    featured: true,
    rating: 4.9,
    reviews: 32,
  }),
  createProduct({
    id: 14,
    name: "Limestone Morning Pleated Cravat",
    category: "Pleated Cravats",
    price: 155,
    image: "/img/p14-stone.jpg",
    description: [
      "A soft stone cravat with understated texture for pale, layered summer dressing.",
      "Its muted finish makes the sculptural pleats the focus and pairs naturally with linen shirting.",
    ],
    weave: "Micro-pleated faille",
    hardware: "Satin nickel slider",
    tags: ["stone", "neutral", "summer", "linen-look"],
    color: "Limestone",
    material: "Viscose-silk faille",
    stock: 20,
    rating: 4.7,
    reviews: 17,
  }),
  createProduct({
    id: 15,
    name: "Champagne Soirée Silk Cravat",
    category: "Evening Cravats",
    price: 190,
    image: "/img/p15-champagne.jpg",
    description: [
      "A champagne silk cravat with a luminous face developed for evening receptions and modern ceremony.",
      "The tonal gold slider keeps the finish polished while allowing the neckline to remain relaxed.",
    ],
    dimensions: "130 × 19 cm",
    weave: "Pleated silk satin",
    hardware: "Champagne gold slider",
    tags: ["champagne", "wedding", "evening", "silk"],
    color: "Soft Champagne",
    material: "100% silk satin",
    stock: 7,
    featured: true,
    rating: 4.9,
    reviews: 26,
  }),
  createProduct({
    id: 16,
    name: "Aubergine Archive Silk Cravat",
    category: "Silk Cravats",
    price: 175,
    image: "/img/p16-aubergine.jpg",
    description: [
      "A dark aubergine cravat informed by the deep, complex colours found in vintage menswear archives.",
      "Its restrained lustre and compact pleats make rich colour easy to wear.",
    ],
    dimensions: "130 × 18 cm",
    weave: "Pleated silk twill",
    hardware: "Dark brass slider",
    tags: ["aubergine", "archive", "silk", "autumn"],
    color: "Deep Aubergine",
    material: "100% silk twill",
    stock: 11,
    rating: 4.8,
    reviews: 20,
  }),
  createProduct({
    id: 17,
    name: "Ink Column Pleated Cravat",
    category: "Pleated Cravats",
    price: 160,
    image: "/img/p17-ink.jpg",
    description: [
      "An inky blue-black cravat with crisp vertical pleats and a cool, architectural presence.",
      "Close in tone to navy yet distinctly deeper, it is designed for effortless tonal layering.",
    ],
    weave: "Micro-pleated satin",
    hardware: "Blackened nickel slider",
    tags: ["ink", "navy", "minimal", "pleated"],
    color: "Ink Blue",
    material: "Silk-viscose satin",
    stock: 19,
    rating: 4.7,
    reviews: 22,
  }),
  createProduct({
    id: 18,
    name: "Coral Terrace Silk Cravat",
    category: "Silk Cravats",
    price: 170,
    image: "/img/p18-coral.jpg",
    description: [
      "A softened coral cravat developed to bring warmth to summer whites, faded denim and unlined jackets.",
      "The silk twill pleats hold a crisp line while feeling light against the skin.",
    ],
    dimensions: "130 × 18 cm",
    weave: "Pleated silk twill",
    hardware: "Pale brass slider",
    tags: ["coral", "summer", "silk", "colour"],
    color: "Terrace Coral",
    material: "100% silk twill",
    stock: 13,
    rating: 4.6,
    reviews: 15,
  }),
  createProduct({
    id: 19,
    name: "Moss Courtyard Textured Cravat",
    category: "Textured Cravats",
    price: 170,
    image: "/img/p19-moss.jpg",
    description: [
      "A moss-green cravat in a gently slubbed silk-linen weave with a naturally dry handle.",
      "It complements tweed, brushed wool and weathered cotton without feeling overly formal.",
    ],
    dimensions: "126 × 19 cm",
    weave: "Pleated silk-linen weave",
    hardware: "Aged bronze slider",
    tags: ["moss", "textured", "linen", "weekend"],
    color: "Courtyard Moss",
    material: "Silk and linen blend",
    stock: 9,
    rating: 4.8,
    reviews: 13,
  }),
  createProduct({
    id: 20,
    name: "Ice Pavilion Pleated Cravat",
    category: "Pleated Cravats",
    price: 160,
    image: "/img/p20-ice.jpg",
    description: [
      "A pale ice-blue cravat whose cool satin finish brightens summer shirting and soft grey tailoring.",
      "Its fine pleats create texture at a distance while remaining discreet in close tonal looks.",
    ],
    weave: "Micro-pleated satin",
    hardware: "Brushed silver slider",
    tags: ["ice blue", "summer", "pleated", "ceremony"],
    color: "Pavilion Ice",
    material: "Silk-viscose satin",
    stock: 12,
    rating: 4.7,
    reviews: 18,
  }),
  createProduct({
    id: 21,
    name: "Noir Gilded Evening Cravat",
    category: "Evening Cravats",
    price: 195,
    image: "/img/p21-noir-gold.jpg",
    description: [
      "A black evening cravat finished with a warm gilded slider for controlled contrast.",
      "Densely pleated satin lends depth to a monochrome dinner jacket as it moves through the light.",
    ],
    dimensions: "128 × 19 cm",
    weave: "Dense pleated silk satin",
    hardware: "Polished gold-tone slider",
    tags: ["black", "gold", "evening", "limited"],
    color: "Noir and Gold",
    material: "100% silk satin",
    stock: 6,
    featured: true,
    rating: 5,
    reviews: 23,
  }),
  createProduct({
    id: 22,
    name: "Midnight Weave Jacquard Cravat",
    category: "Jacquard Cravats",
    price: 185,
    image: "/img/p22-midnight-jacquard.jpg",
    description: [
      "A midnight jacquard cravat whose quiet woven motif appears as light moves across its pleated surface.",
      "Rich in detail but restrained in colour, it rewards a closer look without dominating the outfit.",
    ],
    dimensions: "128 × 19 cm",
    weave: "Pleated tonal jacquard",
    hardware: "Antique brass slider",
    tags: ["midnight", "jacquard", "woven", "evening"],
    color: "Midnight Jacquard",
    material: "Silk-blend jacquard",
    stock: 8,
    featured: true,
    rating: 4.9,
    reviews: 17,
  }),
  createProduct({
    id: 23,
    name: "Oxblood Heritage Cravat",
    category: "Textured Cravats",
    price: 180,
    image: "/img/p23-oxblood.jpg",
    description: [
      "An oxblood cravat woven with a discreet grain that recalls the depth of well-kept leather.",
      "Its dark red tone is a confident alternative to navy or black for evening dressing.",
    ],
    dimensions: "126 × 19 cm",
    weave: "Pleated silk faille",
    hardware: "Dark brass slider",
    tags: ["oxblood", "heritage", "textured", "evening"],
    color: "Heritage Oxblood",
    material: "100% silk faille",
    stock: 10,
    rating: 4.8,
    reviews: 16,
  }),
  createProduct({
    id: 24,
    name: "Silver Blue Passage Cravat",
    category: "Evening Cravats",
    price: 185,
    image: "/img/p24-silver-blue.jpg",
    description: [
      "A silver-blue cravat with a softly reflective surface that moves between cool grey and pale blue.",
      "Designed for modern ceremony, it brings light to navy, charcoal and winter-white tailoring.",
    ],
    dimensions: "130 × 19 cm",
    weave: "Pleated silk satin",
    hardware: "Polished silver slider",
    tags: ["silver blue", "ceremony", "silk", "occasion"],
    color: "Silver Blue",
    material: "100% silk satin",
    stock: 7,
    rating: 4.9,
    reviews: 19,
  }),
];

export default productData;
